import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomLink from '@gc-npm/components.custom-link';
import TextInfo, {
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SIZE,
} from '@gc-npm/components.text';
import Button, { BUTTON_WIDTH, BUTTON_TYPE, BUTTON_HEIGHT } from '@gc-npm/components.button';
import Glyph from '@gc-npm/components.icon-glyph';
import Card from '@/components/Card';
import { getCardTypesBySubSection, getLmelineToSelect } from '@/utils/index';
import UpgradePrices from '@/components/UpgradePrices';
import Carousel from '@gc-npm/components.carousel';
import { LAYOUT_TYPES, SCREEN_TYPES } from '@/utils/constants';

const COMPONENT_NAME = 'SubSection';

const SubSection = ({
  foldedSection,
  subSectionKey,
  subSectionTitle,
  itemsTorender,
  cmsHandler,
  fake,
  screenType,
  blockName,
  activeSlideConectivityId,
}) => {
  const lme = useSelector((state) => state.modulos?.conectividad?.lme);
  const layout = useSelector((state) => state.UI.layout);

  const [isFoldedSection, setIsFoldedSection] = useState(true);

  const conexionFilterOptions = useMemo(() => {
    if (!Array.isArray(itemsTorender) || subSectionKey !== 'conexion') return [];

    const seen = new Set();
    const opts = [];

    for (const it of itemsTorender) {
      if (it?.subcategory !== 'conexion') continue;
      const v = it?.filter;
      if (typeof v !== 'string' || v.length === 0) continue;
      if (seen.has(v)) continue;
      seen.add(v);
      opts.push(v);
    }

    return opts;
  }, [itemsTorender, subSectionKey]);

  // ✅ Filter "mandatorio" por selected:true (si existe)
  const selectedConexionFilter = useMemo(() => {
    if (subSectionKey !== 'conexion') return null;
    if (!Array.isArray(itemsTorender) || itemsTorender.length === 0) return null;

    const selectedItem = itemsTorender.find(
      (it) =>
        it?.subcategory === 'conexion'
        && it?.selected === true
        && typeof it?.filter === 'string'
        && it.filter.length > 0,
    );

    return selectedItem?.filter ?? null;
  }, [subSectionKey, itemsTorender]);

  const [activeConexionFilter, setActiveConexionFilter] = useState(null);


  useEffect(() => {
    if (subSectionKey !== 'conexion') return;
    if (!selectedConexionFilter) return;

    setActiveConexionFilter((prev) => (prev === selectedConexionFilter ? prev : selectedConexionFilter));
  }, [subSectionKey, selectedConexionFilter]);


  useEffect(() => {
    if (subSectionKey !== 'conexion') return;
    if (activeConexionFilter) return;

    const fallback =
      selectedConexionFilter
      ?? conexionFilterOptions[0]
      ?? null;

    if (fallback) setActiveConexionFilter(fallback);
  }, [subSectionKey, activeConexionFilter, selectedConexionFilter, conexionFilterOptions]);

  const effectiveActiveConexionFilter = conexionFilterOptions.includes(activeConexionFilter)
    ? activeConexionFilter
    : selectedConexionFilter ?? conexionFilterOptions[0] ?? null;

  const filteredItemsToRender = useMemo(() => {
    if (!Array.isArray(itemsTorender)) return [];
    if (subSectionKey !== 'conexion') return itemsTorender;
    if (!effectiveActiveConexionFilter) return itemsTorender;

    return itemsTorender.filter((it) => {
      if (it?.subcategory !== 'conexion') return true;
      return it?.filter === effectiveActiveConexionFilter;
    });
  }, [itemsTorender, subSectionKey, effectiveActiveConexionFilter]);

  const { someCardPromoted, someCardHired } = getCardTypesBySubSection(filteredItemsToRender);

  let mobileLines = null;
  if (subSectionKey === 'conexion') {
    mobileLines = getLmelineToSelect(lme);
  }

  const subsectionItemsHired =
    isFoldedSection
    && filteredItemsToRender
    && Array.isArray(filteredItemsToRender)
    && filteredItemsToRender.filter((item) => item.hired).length;

  const subSectionItemsText = subsectionItemsHired
    ? `${subsectionItemsHired} ${subsectionItemsHired > 1 ? 'contratados' : 'contratado'}`
    : '';

  const foldedSectionIcon = isFoldedSection ? 'CheuronDown' : 'CheuronUp';

  if (!itemsTorender || !Array.isArray(itemsTorender) || !itemsTorender.length > 0) {
    return null;
  }

  const taggingInfo = {
    componentName: COMPONENT_NAME,
    componentContent: '',
    componentProduct: '',
    componentVariation: 'all',
  };

  const onclickFoldSection = (param) => {
    setIsFoldedSection(param);
  };


  const onClickConexionFilter = (filterValue) => {
    setActiveConexionFilter(filterValue);
  };

  return (
    <div key={subSectionKey} className="subsection">
      {subSectionTitle && blockName !== 'conectividad' && (
        <div className={`${fake ? 'skeleton' : ''} subsectionTitle`}>
          {!foldedSection && (
            <div>
              <TextInfo
                text={subSectionTitle}
                size={TEXT_SIZE.TITLE6}
                color={TEXT_COLOR.GREY5}
                kind={TEXT_KIND.HEADING}
                fontType={TEXT_FONT_WEIGHT.BOLD}
                level={TEXT_HEADING_LEVEL.H3}
              />
              {subSectionKey === 'cine-y-series' && <UpgradePrices />}
            </div>
          )}

          {foldedSection && (
            <>
              <CustomLink
                taggingInfo={{
                  ...taggingInfo,
                  componentContent: `${subSectionTitle} ${isFoldedSection ? 'desplegar ' : 'plegar'}`,
                  componentProduct: subSectionTitle,
                }}
                onClick={() => onclickFoldSection(!isFoldedSection)}
              >
                <div className="foldingSection">
                  <TextInfo
                    text={subSectionTitle}
                    size={TEXT_SIZE.TITLE7}
                    color={TEXT_COLOR.MOVISTAR_BLUE}
                    kind={TEXT_KIND.HEADING}
                    level={TEXT_HEADING_LEVEL.H3}
                  />
                  <Glyph id={foldedSectionIcon} height={12} width={12} color={TEXT_COLOR.MOVISTAR_BLUE} />
                </div>
              </CustomLink>

              {subSectionItemsText && (
                <TextInfo
                  text={subSectionItemsText}
                  size={TEXT_SIZE.BODY1}
                  color={TEXT_COLOR.GREY5}
                  kind={TEXT_KIND.HEADING}
                />
              )}
            </>
          )}
        </div>
      )}

      {subSectionKey === 'conexion' && conexionFilterOptions.length > 1 && (
        <div className="conexion-filters">
          {conexionFilterOptions.map((filterValue) => (
            <Button
              key={`conexion-filter-${filterValue}`}
              label={filterValue}
              onClick={() => onClickConexionFilter(filterValue)}
              taggingInfo={{
                ...taggingInfo,
                componentContent: `conexion-filter-${filterValue}`,
                componentProduct: filterValue,
              }}
              width={BUTTON_WIDTH.SHORT}
              height={BUTTON_HEIGHT.SMALL}
              type={effectiveActiveConexionFilter === filterValue ? BUTTON_TYPE.PRIMARY_REVERSE : BUTTON_TYPE.COLLAPSED}
            />
          ))}
        </div>
      )}

      <div className={`${subSectionKey === 'conexion' && screenType === SCREEN_TYPES.DESKTOP ? 'modulosCarousel' : 'modulos'}`}>
        {((foldedSection && !isFoldedSection) || !foldedSection) && (
          <>
            {subSectionKey === 'conexion' && screenType === SCREEN_TYPES.DESKTOP && (
              <div>
                <Carousel
                  slideSeparation={8}
                  breakpoint={820}
                  maxPointNum={0}
                  className="carousel-Comunicaciones"
                  activeSlideId={activeSlideConectivityId}
                >
                  {filteredItemsToRender.map((item) => (
                    <Card
                      key={`modulo-${subSectionKey}-${item.id}`}
                      data={{
                        ...item,
                        someCardPromoted,
                        someCardHired,
                        mobileLines,
                      }}
                      fake={fake}
                      cmsHandler={cmsHandler}
                      screenType={screenType}
                      carouselMode
                    />
                  ))}
                </Carousel>
              </div>
            )}

            {(subSectionKey !== 'conexion' || screenType !== SCREEN_TYPES.DESKTOP) &&
              filteredItemsToRender.map((item) => (
                <Card
                  key={`modulo-${subSectionKey}-${item.id}`}
                  data={{
                    ...item,
                    someCardPromoted,
                    someCardHired,
                    mobileLines,
                  }}
                  fake={fake}
                  cmsHandler={cmsHandler}
                  screenType={screenType}
                />
              ))}
          </>
        )}
      </div>

      <style jsx>
        {`
          .subsection {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            .conexion-filters {
              display: flex;
              flex-direction: row;
              gap: 0.5rem;
              flex-wrap: wrap;
              align-items: center;
              margin-top: 0.5rem;
            }

            .modulos {
              display: flex;
              flex-direction: row;
              gap: 1rem;
              flex-wrap: wrap;
              align-items: stretch;
            }

            .subsectionTitle {
              max-width: fit-content;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              gap: 1rem;

              .foldingSection {
                display: flex;
                flex-direction: row;
                gap: 1rem;
                align-items: center;
              }
            }
          }

          @media screen and (max-width: 768px) {
            .subsection {
              .conexion-filters {
                gap: 0.5rem;
              }

              .modulos {
                gap: 0.5rem;
                justify-content: space-between;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default SubSection;
